import axios from 'axios';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Smart API base URL detection for CloudFlare Pages environment
const getApiBaseUrl = (): string => {
  // Check if we're in CloudFlare Pages environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // If on CloudFlare Pages domain, use relative proxy path
    if (hostname.includes('.pages.dev') || hostname.includes('qutec.it')) {
      return '/api/v1';
    }
    
    // If on localhost/development, check for environment variable
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return import.meta.env.VITE_API_BASE_URL || '/api/v1';
    }
  }
  
  // Default fallback to relative path
  return '/api/v1';
};

axios.defaults.baseURL = getApiBaseUrl();

// Add request interceptor to include authentication headers
axios.interceptors.request.use(
  (config) => {
    // Add user email header if available
    try {
      const userEmail = localStorage.getItem('kms_user_email');
      if (userEmail) {
        // Remove quotes if present
        let email = userEmail;
        if (email.startsWith('"') && email.endsWith('"')) {
          email = email.substring(1, email.length - 1);
        }
        config.headers['X-KMS-User-Email'] = email;
      }
    } catch (e) {
      console.error('Error adding user email to axios headers:', e);
    }
    
    // Add cache control headers for auth-related requests
    if (config.url?.includes('/auth/') || config.url?.includes('/user-config/') || config.url?.includes('/vectorstore/')) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('Axios request failed with authentication error:', error.response?.status);
      
      // Check if we're not in an auth flow to prevent redirect loops
      const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
      const isLoginPage = window.location.pathname === '/login';
      const isAuthCallback = window.location.pathname.includes('/auth/callback');
      
      if (!authFlowInProgress && !isLoginPage && !isAuthCallback) {
        // Check for recent redirects to prevent loops
        const lastRedirectTime = sessionStorage.getItem('last_auth_redirect_time');
        const currentTime = Date.now();
        
        if (!lastRedirectTime || (currentTime - parseInt(lastRedirectTime)) > 10000) {
          sessionStorage.setItem('last_auth_redirect_time', currentTime.toString());
          
          // Clear auth data and redirect to login
          localStorage.removeItem('kms_auth_user');
          localStorage.removeItem('kms_auth_check');
          
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default axios; 